import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, 
  AlertTriangle, 
  TrendingUp, 
  DollarSign, 
  Calendar, 
  Settings,
  CheckCircle,
  AlertCircle,
  Filter
} from 'lucide-react';

// Define notification type interface with precise type definitions
interface Notification {
  id: string;
  type: 'alert' | 'success' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: Date;
  isRead: boolean;
  category: 'spending' | 'investment' | 'budget' | 'subscription';
}

// Define animation variants
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.3 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 }
};

const Notifications = () => {
  // Initialize state with proper TypeScript type annotations
  const [activeFilter, setActiveFilter] = useState<string>('all');
  
  // Initialize notifications state with proper type annotation and initial data
  const [notifications] = useState<Array<Notification>>([
    {
      id: '1',
      type: 'alert',
      title: 'Unusual Spending Detected',
      message: "We noticed higher than usual spending in your entertainment category.",
      timestamp: new Date(Date.now() - 1000 * 60 * 30),
      isRead: false,
      category: 'spending'
    },
    {
      id: '2',
      type: 'success',
      title: 'Savings Goal Reached',
      message: "Congratulations! You have reached your emergency fund goal of $10,000! 🎉",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
      isRead: false,
      category: 'budget'
    },
    {
      id: '3',
      type: 'warning',
      title: 'Subscription Renewal',
      message: "Your Netflix subscription will renew in 3 days.",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
      isRead: true,
      category: 'subscription'
    },
    {
      id: '4',
      type: 'info',
      title: 'Investment Opportunity',
      message: 'Based on your profile, consider diversifying your portfolio with these ETFs.',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48),
      isRead: true,
      category: 'investment'
    }
  ]);

  // Initialize notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    spending: true,
    investment: true,
    budget: true,
    subscription: true,
    pushNotifications: true,
    emailNotifications: true,
    smsNotifications: false
  });

  // Helper function to get appropriate icon for notification type
  const getIcon = (type: string) => {
    switch (type) {
      case 'alert':
        return <AlertCircle className="text-red-500" size={24} />;
      case 'success':
        return <CheckCircle className="text-green-500" size={24} />;
      case 'warning':
        return <AlertTriangle className="text-orange-500" size={24} />;
      case 'info':
        return <TrendingUp className="text-blue-500" size={24} />;
      default:
        return <Bell className="text-[#68A511]" size={24} />;
    }
  };

  // Helper function to format timestamp into relative time
  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 1000 / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Available filters for notifications
  const filters = [
    { id: 'all', label: 'All', icon: Bell },
    { id: 'spending', label: 'Spending', icon: DollarSign },
    { id: 'investment', label: 'Investment', icon: TrendingUp },
    { id: 'budget', label: 'Budget', icon: Calendar },
    { id: 'subscription', label: 'Subscriptions', icon: Settings }
  ];

  // Filter notifications based on active filter
  const filteredNotifications = notifications.filter(notification => 
    activeFilter === 'all' || notification.category === activeFilter
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Notifications</h2>
        <button className="p-2 rounded-lg bg-[#DCE9C2] hover:bg-[#68A511] text-[#598615] hover:text-white transition-all duration-300">
          <Settings size={20} />
        </button>
      </div>

      {/* Filters Section */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="bg-white/50 backdrop-blur-md rounded-2xl p-4 shadow-xl"
      >
        <div className="flex items-center gap-4 overflow-x-auto pb-2">
          {filters.map((filter) => {
            const Icon = filter.icon;
            return (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
                  activeFilter === filter.id
                    ? 'bg-[#68A511] text-white'
                    : 'bg-[#DCE9C2] text-[#598615] hover:bg-[#68A511] hover:text-white'
                }`}
              >
                <Icon size={16} />
                {filter.label}
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* Notifications List */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="bg-white/50 backdrop-blur-md rounded-2xl p-6 shadow-xl"
      >
        <AnimatePresence>
          <div className="space-y-4">
            {filteredNotifications.map((notification) => (
              <motion.div
                key={notification.id}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className={`p-4 rounded-xl transition-all duration-300 cursor-pointer ${
                  notification.isRead
                    ? 'bg-[#DCE9C2]/50'
                    : 'bg-[#DCE9C2]'
                }`}
              >
                <div className="flex items-start gap-4">
                  {getIcon(notification.type)}
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <h3 className="font-medium text-gray-700">
                        {notification.title}
                      </h3>
                      <span className="text-sm text-gray-500">
                        {formatTime(notification.timestamp)}
                      </span>
                    </div>
                    <p className="text-gray-600 mt-1">
                      {notification.message}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </AnimatePresence>
      </motion.div>

      {/* Settings Section */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="bg-white/50 backdrop-blur-md rounded-2xl p-6 shadow-xl"
      >
        <h2 className="text-xl font-semibold text-gray-700 mb-6">
          Notification Settings
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="font-medium text-gray-600">Categories</h3>
            {Object.entries(notificationSettings)
              .filter(([key]) => ['spending', 'investment', 'budget', 'subscription'].includes(key))
              .map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="capitalize text-gray-700">
                    {key}
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={() => setNotificationSettings(prev => ({
                        ...prev,
                        [key]: !prev[key]
                      }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#68A511]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#68A511]"></div>
                  </label>
                </div>
              ))}
          </div>
          
          <div className="space-y-4">
            <h3 className="font-medium text-gray-600">Delivery Methods</h3>
            {['pushNotifications', 'emailNotifications', 'smsNotifications'].map((key) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-gray-700">
                  {key === 'pushNotifications' ? 'Push Notifications' :
                   key === 'emailNotifications' ? 'Email Notifications' :
                   'SMS Notifications'}
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings[key as keyof typeof notificationSettings]}
                    onChange={() => setNotificationSettings(prev => ({
                      ...prev,
                      [key]: !prev[key as keyof typeof notificationSettings]
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#68A511]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#68A511]"></div>
                </label>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Notifications;